"use client";

import React from "react";
import {
    Button,
    type ButtonProps,
    createPolymorphicComponent,
    type PolymorphicComponentProps,
} from "@mantine/core";
import clsx from "clsx";
import styles from "./AppButton.module.scss";

type AppButtonTheme = "primary" | "secondary";

type AppButtonBaseProps = ButtonProps & {
    theme?: AppButtonTheme;
};

function variantToClass(theme: AppButtonTheme, variant?: ButtonProps["variant"]) {
    const v = variant ?? "filled";

    if (theme === "secondary") {
        if (v === "outline") return styles.secondaryOutline;
        if (v === "light") return styles.secondaryLight;
        return styles.secondaryFilled;
    }

    if (v === "outline") return styles.primaryOutline;
    if (v === "light") return styles.primaryLight;
    return styles.primaryFilled;
}

type AppButtonComponent = <C extends React.ElementType = "button">(
    props: PolymorphicComponentProps<C, AppButtonBaseProps>
) => React.ReactElement;

const _AppButton = React.forwardRef<HTMLButtonElement, any>(function AppButton(
    { theme = "primary", variant, className, classNames, ...rest }: AppButtonBaseProps,
    ref
) {
    return (
        <Button
            {...rest}
            ref={ref}
            variant={variant}
            classNames={classNames}
            className={clsx(styles.root, variantToClass(theme, variant), className)}
        />
    );
});

// This restores Mantine polymorphic props (component, href, etc.)
export const AppButton = createPolymorphicComponent<"button", AppButtonBaseProps>(
    _AppButton as unknown as AppButtonComponent
);